import { LanguageVariantsSection } from "@/components/language-variants-section";
import { BuilderSection } from "@/components/studio/builder-section";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import { UploadDropzone } from "@/components/studio/upload-dropzone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import { ContentAlbumService } from "@/lib/api/album.service";
import { categoryService } from "@/lib/api/categories.service";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { queryClient } from "@/lib/queryClient";
import type { Category, TagGroup, TagOption } from "@/lib/schema";
import { SUPPORTED_LANGUAGES, getLanguageLabel } from "@/lib/studio-constants";
import {
  THUMBNAIL_ACCEPT_ATTR,
  THUMBNAIL_HINT,
  validateThumbnailFile,
} from "@/lib/thumbnail-validation";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Album,
  ArrowLeft,
  Globe,
  Loader2,
  Music,
  Save,
  Video,
  X,
} from "lucide-react";
import { useCallback, useState } from "react";
import { Link, useLocation } from "wouter";

interface AlbumVariantDraft {
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string;
}

export default function AlbumCreatePage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { uploadFile, isUploading } = useUpload({
    onSuccess: (response) => {
      setFormCoverImage(response.objectPath);
    },
  });

  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formAlbumType, setFormAlbumType] = useState("");
  const [formCategoryId, setFormCategoryId] = useState("");
  const [formCoverImage, setFormCoverImage] = useState("");
  const [formTagOptionIds, setFormTagOptionIds] = useState<string[]>([]);

  const [variants, setVariants] = useState<AlbumVariantDraft[]>([
    {
      languageCode: "en",
      isDefault: true,
      localizedTitle: "",
      localizedDescription: "",
    },
  ]);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });
  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });
  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const getGroupOptions = useCallback(
    (groupId: string) =>
      tagOptions.filter((o) => o.groupId === groupId && o.status === "active"),
    [tagOptions],
  );

  const toggleTagOption = (tagId: string) => {
    setFormTagOptionIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId],
    );
  };

  const activeVariant = variants[activeVariantIndex];
  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !variants.some((v) => v.languageCode === lang.code),
  );

  function addVariant(langCode: string) {
    setVariants((prev) => [
      ...prev,
      {
        languageCode: langCode,
        isDefault: false,
        localizedTitle: "",
        localizedDescription: "",
      },
    ]);
    setActiveVariantIndex(variants.length);
  }

  function setAsDefault(index: number) {
    setVariants((prev) =>
      prev.map((v, i) => ({ ...v, isDefault: i === index })),
    );
  }

  function confirmRemoveVariant() {
    if (pendingRemoveIndex === null) return;
    const removing = variants[pendingRemoveIndex];
    setVariants((prev) => {
      const next = prev.filter((_, i) => i !== pendingRemoveIndex);
      if (removing?.isDefault && next.length > 0) {
        next[0] = { ...next[0], isDefault: true };
      }
      return next;
    });
    setActiveVariantIndex(0);
    setPendingRemoveIndex(null);
  }

  function updateVariantField(field: keyof AlbumVariantDraft, value: string) {
    setVariants((prev) =>
      prev.map((v, i) =>
        i === activeVariantIndex ? { ...v, [field]: value } : v,
      ),
    );
  }

  async function handleCoverUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = await validateThumbnailFile(file);
    if (!validation.valid) {
      toast({
        title: "Invalid image",
        description: validation.error,
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }
    await uploadFile(file);
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      const album = await ContentAlbumService.create({
        name: formName,
        description: formDescription || null,
        albumType: formAlbumType as "audio" | "video",
        categoryId:
          formCategoryId && formCategoryId !== "none" ? formCategoryId : null,
        tagOptionIds: formTagOptionIds,
        coverImage: formCoverImage || null,
        primaryLanguageCode:
          variants.find((v) => v.isDefault)?.languageCode || "en",
      });

      // Skip the primary variant — backend already creates it during album creation
      const primaryLang =
        variants.find((v) => v.isDefault)?.languageCode || "en";
      for (const v of variants) {
        if (v.languageCode === primaryLang) continue;
        await ContentAlbumService.createVariant(album.id, {
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          localizedTitle: v.localizedTitle || null,
          localizedDescription: v.localizedDescription || null,
        });
      }

      return album;
    },
    onSuccess: (album) => {
      queryClient.invalidateQueries({ queryKey: ["content-albums"] });
      toast({ title: "Album created successfully" });
      setLocation(`/studio/albums/${album.id}/edit`);
    },
    onError: () => {
      toast({ title: "Failed to create album", variant: "destructive" });
    },
  });

  const isSaving = createMutation.isPending;
  const hasEmptyNonPrimaryVariant = variants.some(
    (v) => !v.isDefault && !v.localizedTitle.trim(),
  );

  return (
    <div
      className="p-6 space-y-6 max-w-4xl mx-auto"
      data-testid="album-create-page"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <Link href="/studio/albums">
          <Button variant="ghost" size="icon" data-testid="button-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <h1
            className="font-serif text-2xl font-semibold text-foreground"
            data-testid="text-page-title"
          >
            Create Album
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Curated collection for audio or video content
          </p>
        </div>
        <Button
          onClick={() => createMutation.mutate()}
          disabled={
            !formName.trim() ||
            !formAlbumType ||
            hasEmptyNonPrimaryVariant ||
            isSaving
          }
          data-testid="button-save"
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Create Album
        </Button>
      </div>

      <BuilderSection
        icon={Album}
        title="Basic Info"
        description="Name, type, description, and cover image"
        defaultOpen
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="album-name">Name *</Label>
            <Input
              id="album-name"
              placeholder="e.g. Morning Calm, Evening Wind Down"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              data-testid="input-album-name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="album-type">Album Type *</Label>
            <Select value={formAlbumType} onValueChange={setFormAlbumType}>
              <SelectTrigger id="album-type" data-testid="select-album-type">
                <SelectValue placeholder="Choose album type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="audio">
                  <span className="flex items-center gap-2">
                    <Music className="h-4 w-4 text-blue-500" /> Audio
                  </span>
                </SelectItem>
                <SelectItem value="video">
                  <span className="flex items-center gap-2">
                    <Video className="h-4 w-4 text-purple-500" /> Video
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="album-description">Description</Label>
            <Textarea
              id="album-description"
              placeholder="A short description of this album..."
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              className="resize-none"
              rows={3}
              data-testid="input-album-description"
            />
          </div>

          <div className="space-y-2">
            <Label>Cover Image</Label>
            {formCoverImage ? (
              <div className="relative inline-block">
                <img
                  src={formCoverImage}
                  alt="Cover preview"
                  className="w-40 h-24 rounded-md object-cover"
                />
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                  onClick={() => setFormCoverImage("")}
                  data-testid="button-remove-cover"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <UploadDropzone
                type="image"
                label="Upload Cover"
                hint={THUMBNAIL_HINT}
                accept={THUMBNAIL_ACCEPT_ATTR}
                onChange={handleCoverUpload}
                isUploading={isUploading}
                testId="input-cover-upload"
              />
            )}
          </div>
        </div>
      </BuilderSection>

      <IntentTaggingSection
        categories={categories}
        categoryId={formCategoryId}
        onCategoryChange={setFormCategoryId}
        tagGroups={tagGroups}
        getGroupOptions={getGroupOptions}
        selectedTagOptionIds={formTagOptionIds}
        onToggleTagOption={toggleTagOption}
      />

      <BuilderSection
        icon={Globe}
        title="Language Variants"
        description="Translations for album title and description"
        badge={`${variants.length} language${variants.length !== 1 ? "s" : ""}`}
        defaultOpen
      >
        <LanguageVariantsSection
          variants={variants}
          activeVariantIndex={activeVariantIndex}
          onActiveVariantChange={setActiveVariantIndex}
          availableLanguages={availableLanguages}
          onAddVariant={addVariant}
          onSetDefault={setAsDefault}
          pendingRemoveIndex={pendingRemoveIndex}
          onRequestRemove={setPendingRemoveIndex}
          onConfirmRemove={confirmRemoveVariant}
          onCancelRemove={() => setPendingRemoveIndex(null)}
        >
          {activeVariant && (
            <div className="space-y-3 pt-3">
              {activeVariant.isDefault ? (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Primary language ({getLanguageLabel(activeVariant.languageCode)}) uses the Name and Description from Basic Info.
                  </p>
                  <div className="space-y-2">
                    <Label>Localized Title</Label>
                    <Input
                      value={formName}
                      readOnly
                      disabled
                      placeholder={`Title in ${getLanguageLabel(activeVariant.languageCode)}`}
                      data-testid={`input-localized-title-${activeVariant.languageCode}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Localized Description</Label>
                    <Textarea
                      value={formDescription}
                      readOnly
                      disabled
                      placeholder={`Description in ${getLanguageLabel(activeVariant.languageCode)}`}
                      rows={2}
                      data-testid={`input-localized-description-${activeVariant.languageCode}`}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>
                      Localized Title
                      <span className="text-destructive ml-1">*</span>
                    </Label>
                    <Input
                      value={activeVariant.localizedTitle}
                      onChange={(e) =>
                        updateVariantField("localizedTitle", e.target.value)
                      }
                      placeholder={`Title in ${getLanguageLabel(activeVariant.languageCode)}`}
                      data-testid={`input-localized-title-${activeVariant.languageCode}`}
                    />
                    {!activeVariant.localizedTitle.trim() && (
                      <p className="text-xs text-destructive">
                        Title required for non-default language.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Localized Description</Label>
                    <Textarea
                      value={activeVariant.localizedDescription}
                      onChange={(e) =>
                        updateVariantField("localizedDescription", e.target.value)
                      }
                      placeholder={`Description in ${getLanguageLabel(activeVariant.languageCode)}`}
                      rows={2}
                      data-testid={`input-localized-description-${activeVariant.languageCode}`}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </LanguageVariantsSection>
      </BuilderSection>
    </div>
  );
}
